import { useMemo } from "react";
import { Modal } from "react-bootstrap";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveHeatMap } from "@nivo/heatmap";
import { ObbAnnotation } from "./api/Application";
import { Button } from "./components/Button";

interface VehicleStatsModalProps {
    annotations: ObbAnnotation[];
    onClose:     () => void;
}

export function VehicleStatsModal({ annotations, onClose }: VehicleStatsModalProps) {
    const { colorData, typeData, heatmapData, totalAnnotated, totalUnannotated } = useMemo(() => {
        const colorCounts = new Map<string, number>();
        const typeCounts  = new Map<string, number>();
        const comboCounts = new Map<string, Map<string, number>>();
        let annotated   = 0;
        let unannotated = 0;
        for (const ann of annotations) {
            const color = ann.attributes.color;
            const type  = ann.attributes.vehicleType;

            if (!color || !type) { unannotated++; continue; }
            annotated++;

            if (color) colorCounts.set(color, (colorCounts.get(color) ?? 0) + 1);
            if (type)  typeCounts.set(type, (typeCounts.get(type) ?? 0) + 1);

            if (color && type) {
                if (!comboCounts.has(type)) comboCounts.set(type, new Map());
                const inner = comboCounts.get(type)!;
                inner.set(color, (inner.get(color) ?? 0) + 1);
            }
        }

        const colorPalette: Record<string, string> = {
            white:  "#f0f0f0", black:  "#1a1a2e", gray:   "#6b7280",
            silver: "#a8a9ad", red:    "#ef4444", green:  "#22c55e",
            blue:   "#3b82f6", brown:  "#92400e", orange: "#f97316",
            purple: "#a855f7", gold:   "#eab308", yellow: "#facc15",
        };

        const colorData = Array.from(colorCounts.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([id, value]) => ({
                id,
                label: id.charAt(0).toUpperCase() + id.slice(1),
                value,
                color: colorPalette[id.toLowerCase()] ?? "#6b7280",
            }));

        const typePalette = [
            "#6366f1", "#06b6d4", "#10b981", "#f59e0b", "#ef4444",
            "#8b5cf6", "#ec4899", "#14b8a6",
        ];
        const typeData = Array.from(typeCounts.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([id, value], i) => ({
                id,
                label: id,
                value,
                color: typePalette[i % typePalette.length],
            }));

        // Build heatmap: rows = vehicle types, columns = colors
        const allColors = Array.from(colorCounts.keys()).sort();
        const allTypes  = Array.from(typeCounts.keys()).sort();
        const heatmapData = allTypes.map(type => ({
            id: type,
            data: allColors.map(color => ({
                x:     color.charAt(0).toUpperCase() + color.slice(1),
                y:     comboCounts.get(type)?.get(color) ?? 0,
            })),
        }));

        return {
            colorData,
            typeData,
            heatmapData,
            totalAnnotated:   annotated,
            totalUnannotated: unannotated,
        };
    }, [annotations]);

    const hasHeatmap = heatmapData.length > 0 && heatmapData.some(r => r.data.some(d => d.y > 0));

    return (
        <Modal centered size="lg" show={true} onHide={onClose}>
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-semibold">
                    Vehicle Statistics
                    <span className="ms-3 fs-6 text-body-secondary fw-normal">
                        {totalAnnotated} annotated · {totalUnannotated} pending
                    </span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-4 pb-4">
                {/* Pie Charts Row */}
                <div className="d-flex gap-3 mb-4">
                    {/* Color Distribution */}
                    <div className="flex-grow-1 border rounded-3 p-3" style={{ minHeight: 340 }}>
                        <h6 className="text-body-secondary text-uppercase small fw-bold tracking-wide mb-0">
                            Color Distribution
                        </h6>
                        {colorData.length === 0 ? (
                            <div className="d-flex align-items-center justify-content-center h-100 text-body-tertiary">
                                No color data yet
                            </div>
                        ) : (
                            <div style={{ height: 300 }}>
                                <ResponsivePie
                                    data={colorData}
                                    colors={{ datum: "data.color" }}
                                    margin={{ top: 20, right: 90, bottom: 20, left: 90 }}
                                    innerRadius={0.55}
                                    padAngle={1.5}
                                    cornerRadius={4}
                                    activeOuterRadiusOffset={6}
                                    arcLinkLabelsSkipAngle={12}
                                    arcLinkLabelsTextColor="var(--bs-body-color)"
                                    arcLinkLabelsThickness={2}
                                    arcLinkLabelsColor={{ from: "color" }}
                                    arcLabelsSkipAngle={20}
                                    arcLabelsTextColor={{ from: "color", modifiers: [["darker", 2]] }}
                                    motionConfig="gentle"
                                    transitionMode="startAngle"
                                />
                            </div>
                        )}
                    </div>

                    {/* Type Distribution */}
                    <div className="flex-grow-1 border rounded-3 p-3" style={{ minHeight: 340 }}>
                        <h6 className="text-body-secondary text-uppercase small fw-bold tracking-wide mb-0">
                            Type Distribution
                        </h6>
                        {typeData.length === 0 ? (
                            <div className="d-flex align-items-center justify-content-center h-100 text-body-tertiary">
                                No type data yet
                            </div>
                        ) : (
                            <div style={{ height: 300 }}>
                                <ResponsivePie
                                    data={typeData}
                                    colors={{ datum: "data.color" }}
                                    margin={{ top: 20, right: 90, bottom: 20, left: 90 }}
                                    innerRadius={0.55}
                                    padAngle={1.5}
                                    cornerRadius={4}
                                    activeOuterRadiusOffset={6}
                                    arcLinkLabelsSkipAngle={12}
                                    arcLinkLabelsTextColor="var(--bs-body-color)"
                                    arcLinkLabelsThickness={2}
                                    arcLinkLabelsColor={{ from: "color" }}
                                    arcLabelsSkipAngle={20}
                                    arcLabelsTextColor="#ffffff"
                                    motionConfig="gentle"
                                    transitionMode="startAngle"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Cross-Tabulation Heatmap */}
                {hasHeatmap && (
                    <div className="border rounded-3 p-3">
                        <h6 className="text-body-secondary text-uppercase small fw-bold tracking-wide mb-0">
                            Type and Color Matrix
                        </h6>
                        <div style={{ height: Math.max(200, heatmapData.length * 28 + 40) }}>
                            <ResponsiveHeatMap
                                data={heatmapData}
                                margin={{ top: 40, right: 20, bottom: 20, left: 80 }}
                                axisTop={{
                                    tickSize: 0,
                                    tickPadding: 8,
                                }}
                                axisLeft={{
                                    tickSize: 0,
                                    tickPadding: 8,
                                }}
                                colors={{
                                    type:       "sequential",
                                    scheme:     "blues",
                                    minValue:   0,
                                }}
                                emptyColor="#f8f9fa"
                                borderRadius={4}
                                borderWidth={2}
                                inactiveOpacity={0.85}
                                borderColor="var(--bs-modal-bg, #ffffff)"
                                labelTextColor={{ from: "color", modifiers: [["darker", 2.4]] }}
                                hoverTarget="cell"
                                motionConfig="gentle"
                            />
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                <Button className="bg-secondary" onClick={onClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
}